/* @moldura padrao */
/**
 * page-completo.tsx (Inbox) — o arranjo de mesa. Escolhido por `page.tsx`.
 *
 * ─── DE ONDE VEIO ESTA TELA ─────────────────────────────────────────────────
 * Molde: `theme/adminux-react/src/pages/Inbox.tsx`. O AdminUX chama de **Inbox**
 * o que o HTML chama de `adminux-email` — procurar "mail" no React não acha
 * nada (lei visual §2b). O molde é o REACT, nunca o HTML.
 *
 * ─── A COLUNA TEM DOIS GRUPOS, E "CONSULTA" MORREU ──────────────────────────
 * *"Por que consulta? Eu não vou consultar os outros assim? Está ruim. O que eu
 * faria é um divisor para avisos e outro para autorizações."* (30/08)
 *
 * O erro era de categoria: `CONSULTA` descrevia o que se FAZ ali, enquanto as
 * outras entradas nomeavam o que se VÊ. Agora os dois divisores nomeiam
 * **coisas**, que é o que a pessoa procura ao bater o olho. `Tudo` fica fora
 * dos grupos e mostra os dois misturados, por data.
 *
 * ─── A LINHA É A DO E-MAIL, E ISSO FOI PEDIDO ───────────────────────────────
 * *"Os itens são muito diferentes do que a gente vê no e-mail. Tem a setinha
 * para marcar, tem a estrelinha, tem que ter data, tem lá uma hora."*
 *
 * Ordem das colunas, e ela é a do DOM — **sem uma classe `order-*`**:
 *
 *   [check] [estrela] [rosto+selo] [lido] ... [data / há X dias] [arquivar]
 *    <----------- tudo à esquerda ---->        <---- à direita ---->
 *
 * ⚠️ POR QUE NENHUM `order-*`: o Bootstrap gera `order-0` a `order-5`, mais
 * `first` e `last`. `order-6` e `order-7` NÃO EXISTEM, e classe que não existe
 * não dá erro — o elemento cai no `order: 0` e vai para o COMEÇO da linha. Isso
 * aconteceu três vezes nesta tela: o arquivar nasceu antes do checkbox, e
 * depois o texto e a data passaram na frente da foto. Reordenar por classe só
 * se paga quando o celular precisa de ordem diferente — e ele tem tela própria.
 *
 * ⚠️ `adminuiux-list-group` NÃO É DECORAÇÃO: sem ela, `_list.scss` não aplica
 * `border-radius` nem `margin-bottom` nas linhas.
 *
 * ⚠️ O BADGE DE STATUS VEM DE `autorizacoes/shared.ts`. Esta tela tinha
 * inventado o seu (`bg-warning-subtle`), e o resultado era o mesmo "pendente"
 * de duas cores em duas telas. Ele desconfiou sem ver o código: *"acho que
 * esses labels não estão de acordo com a regra de cores"*. Não estavam.
 *
 * ─── O QUE ESTÁ NESTE ARQUIVO E O QUE NÃO ESTÁ ──────────────────────────────
 * Aqui só há ARRANJO. O que cada pasta contém, o contador, o "há 5 dias", a cor
 * da borda e o que a estrela faz vêm de `shared.tsx` — dividido por risco: o
 * que dói se as duas telas divergirem mora lá, porque elas nunca aparecem
 * juntas e ninguém notaria a divergência.
 */
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { CustomAvatarSelo } from '@/components/CustomAvatarSelo';
import { api } from '@/lib/api';
import {
  badge, rotuloStatus, valorResumo, varianteStatus,
  type Autorizacao, type StatusAutorizacao,
} from '@/pages/autorizacoes/shared';
import {
  GRUPOS, ICONE_AUTORIZACAO, TIPOS, TODAS_ENTRADAS,
  destacar, esperaDe, haQuantoTempo, quando, usarInbox,
  type Aviso, type Filtro,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

export default function InboxCompletoPage() {
  const navigate = useNavigate();
  const ix = usarInbox();
  const [confirmarLote, setConfirmarLote] = useState(false);

  // A BARRA LATERAL, copiada do molde React — o `useState` + `useEffect` que o
  // HTML não tem. A limpeza no unmount é do próprio tema, e é necessária: sem
  // ela a classe fica grudada no `body` depois de sair da tela.
  const [lateralFechada, setLateralFechada] = useState(false);
  useEffect(() => {
    document.body.classList.toggle('innermenu-close', lateralFechada);
    return () => document.body.classList.remove('innermenu-close');
  }, [lateralFechada]);

  const {
    filtro, trocarFiltro, carregando, carregar, busca, setBusca, recado, setRecado,
    itens, contador, marcados, setMarcados, alternarMarca, temSelecao,
    temNaoLido, pagina, total, porPagina, paginas, irParaPagina,
    arquivar, alternarLido, marcarTudoLido,
    favoritarAviso, favoritarPedido, marcarLidosMarcados,
  } = ix;

  async function abrir(a: Aviso) {
    if (!a.lida) { try { await api.post(`/avisos/${a.id}/lido`); } catch { /* ignora */ } }
    if (a.link) navigate(a.link);
  }

  function confirmarEArquivar() {
    setConfirmarLote(false);
    confirmarEArquivar();
  }

  // ⚠️ NADA DE `slice` AQUI. A página vem pronta do servidor
  // (`GET /avisos/lista?pagina=`), e `porPagina` sai de
  // `avisos.itens_por_pagina`, na tela de Configurações. Antes esta tela pedia
  // a caixa inteira e fatiava no navegador — a paginação era só aparência, e
  // com 500 avisos eram 500 linhas de JSON por abertura para mostrar quinze.
  const naTela = itens;
  const paginaAtual = pagina;

  /** Os avisos SELECIONÁVEIS da página — pedido não entra (não se arquiva nem
   *  se lê) e arquivado também não. É o alvo do "todos". */
  const avisosDaPagina = naTela
    .map((it) => it.aviso)
    .filter((a): a is Aviso => !!a && a.estado !== 'arquivada');
  const todosMarcados = avisosDaPagina.length > 0 && marcados.length === avisosDaPagina.length;

  // ---- A LINHA DE AVISO ---------------------------------------------------
  //
  // A ORDEM DAS COLUNAS E A DO DOM, e nao ha uma classe `order-*` aqui. Isso e
  // conserto de um defeito meu, cometido DUAS VEZES no mesmo arquivo:
  //
  //   O Bootstrap gera `order-0` ate `order-5`, mais `order-first` e
  //   `order-last`. NAO existe `order-6` nem `order-7` -- e classe que nao
  //   existe nao da erro nenhum: o elemento cai no `order: 0` padrao e vai para
  //   o COMECO da linha.
  //
  // Na primeira vez foi o botao de arquivar, que nasceu antes do checkbox. Na
  // segunda foram o texto (`order-7`) e a data (`order-6`), que passaram na
  // frente da foto -- e foi assim que a foto "nao voltou para a esquerda"
  // depois de eu ter movido a foto para a esquerda.
  //
  // A licao nao e decorar o limite: e que **reordenar por classe so se paga
  // quando o celular precisa de uma ordem diferente do desktop**. Aqui nao
  // precisa: a coluna do texto e `col-12` no celular e quebra linha sozinha.
  // Escrever o DOM na ordem em que se le e a versao que nao tem como falhar em
  // silencio.
  //
  //   [check] [estrela] [foto] [icone] [lido] .... [data/parado] [arquivar]
  //    <---------------- tudo a esquerda ---->      <-- a direita -->
  //
  // "Esses iconezinhos eu acho que tem que ficar tudo na esquerda; so o
  //  arquivar e o horario, data etc. ficam na direita do cartao" (30/08).
  function linhaAviso(a: Aviso) {
    const arquivada = a.estado === 'arquivada';
    const t = quando(a.criado_em);
    const quem = a.origem_nome || a.titulo;
    const parado = haQuantoTempo(a.criado_em);
    return (
      <div
        key={`av-${a.id}`}
        className={`list-group-item ${a.lida ? '' : 'new'} ${esperaDe(a)}`}
      >
        <div className="row casaa-inbox-linha-cols align-items-center">
          <div className="col-auto casaa-inbox-controle">
            <input
              className="form-check-input mt-0"
              type="checkbox"
              aria-label={`Selecionar: ${quem}`}
              checked={marcados.includes(a.id)}
              onChange={() => alternarMarca(a.id)}
              disabled={arquivada}
            />
          </div>
          <div className="col-auto casaa-inbox-controle">
            <button
              type="button"
              className="btn btn-link border-0"
              title={a.favorito ? 'Tirar dos favoritos' : 'Marcar como favorito'}
              aria-pressed={a.favorito}
              onClick={() => favoritarAviso(a)}
            >
              <i className={`bi ${a.favorito ? 'bi-star-fill text-warning' : 'bi-star text-secondary'}`}></i>
            </button>
          </div>
          {/* O ROSTO. "Gostei da foto da Adriana", "essas fotos ficaram legais"
              -- ela responde QUEM, e e o que o olho varre primeiro. */}
          {/* O rosto e o selo, agora peça própria — ver `CustomAvatarSelo`.
              Ela é idêntica nas duas telas da Inbox, e é a única parte do
              cartão que é: o resto tem o mesmo conteúdo e arranjo diferente. */}
          <div className="col-auto">
            <CustomAvatarSelo foto={a.origem_foto} nome={a.origem_nome} icone={a.icone} titulo={a.titulo} />
          </div>
          <div
            className="col-12 col-md"
            role="button"
            onClick={() => abrir(a)}
          >
            <p className={`mb-0 ${a.lida ? '' : 'fw-semibold'}`}>{quem}</p>
            <p className="text-secondary text-truncated mb-0">{destacar(a.texto)}</p>
          </div>
          {/* A DIREITA, e so isto: quando foi, ha quanto tempo esta parado, e a
              acao. `ms-auto` empurra o bloco para a borda quando sobra espaco. */}
          <div className="col-auto text-end ms-auto">
            <p className="mb-0 small">
              {t.dia} <span className="text-secondary">{t.hora}</span>
            </p>
            {parado && !arquivada && !a.lida && (
              <p className={`mb-0 small ${parado.tenso ? 'text-danger' : 'text-secondary'}`}>
                {parado.texto}
              </p>
            )}
          </div>
          {/* AS DUAS AÇÕES SÃO UM PAR, E FICAM NUMA COLUNA SÓ.
              *"Reduz o espaço entre o envelope e o objeto de arquivo — eles
              estão muito afastados, têm que ficar mais próximos."* (30/08)

              Medido antes: 17px entre os desenhos. O vão era a soma de duas
              coisas invisíveis — o `column-gap` da linha (que separa COLUNAS,
              e elas eram duas) mais o padding lateral que o `btn-square` do
              tema dá a cada botão.

              Juntar os dois numa coluna só resolve a primeira metade: eles
              deixam de ser vizinhos separados pelo gutter e passam a ser um
              grupo, com o próprio espaçamento. `casaa-inbox-acoes` cuida da
              segunda, encolhendo o padding.

              ⚠️ O ENVELOPE VEM ANTES do arquivar, e a ordem não é arbitrária:
              marcar como lido é reversível, arquivar tira da lista. O gesto
              mais fácil de desfazer fica mais longe da borda, onde o dedo
              escorrega. */}
          <div className="col-auto d-flex casaa-inbox-acoes">
            {!arquivada && (
              <button
                type="button"
                className="btn btn-sm btn-square btn-link text-secondary"
                title={a.lida ? 'Marcar como não lido' : 'Marcar como lido'}
                onClick={(e) => { e.stopPropagation(); alternarLido(a); }}
              >
                {/* ⚠️ NÃO é `check2-all`: ele leu o duplo tique como
                    "selecionar tudo" duas vezes, em dias diferentes. Envelope
                    fechado = por ler, aberto = lido. */}
                <i className={`bi ${a.lida ? 'bi-envelope-open' : 'bi-envelope-fill text-theme-1'}`}></i>
              </button>
            )}
            {!arquivada && (
              <button
                type="button"
                className="btn btn-sm btn-square btn-link text-secondary"
                title="Arquivar"
                onClick={(e) => { e.stopPropagation(); arquivar(a); }}
              >
                <i className="bi bi-archive"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- A LINHA DE AUTORIZACAO -- e um PEDIDO, nao um aviso ----------------
  // Mesma ordem de colunas da linha de aviso, e isso importa: as duas convivem
  // na pasta "Tudo", e coluna que nao bate entre vizinhas transforma a lista
  // num dente de serra. As duas casas que aqui ficam vazias (o checkbox e o
  // indicador de leitura) nao somem por isso.
  //
  // Sem caixa de selecao **de proposito**: as duas acoes de lote sao arquivar e
  // marcar como lido, e nenhuma existe para um pedido -- ele nao some e nao tem
  // "lida". Checkbox que nao leva a acao nenhuma e promessa falsa.
  function linhaAutorizacao(a: Autorizacao) {
    const t = quando(a.criado_em);
    const st = a.status as StatusAutorizacao;
    const parado = haQuantoTempo(a.criado_em);
    const quemPede = a.solicitante_nome || a.registrante_nome || '';
    // Os DOIS, quando ha os dois: um pedido de 7% sobre R$ 1.000 nao se resume
    // a "7%". E vai em negrito -- e o que ele procura na linha.
    // O MESMO `valorResumo` da tela de Autorizações — ver `shared.ts`.
    const quanto = valorResumo(a);
    return (
      <div
        key={`au-${a.id}`}
        className={`list-group-item casaa-inbox-linha-pedido ${st === 'pendente' ? 'casaa-inbox-espera-decisao' : 'casaa-inbox-espera-fim'}`}
      >
        <div className="row casaa-inbox-linha-cols align-items-center">
          {/* O CHECKBOX EXISTE E ESTÁ DESABILITADO — pedido dele em 30/08:
              *"não pode ser marcado? coloca a checkbox e desabilita, para não
              ficar visualmente esquisito. Eu sei que até é uma dica de que é
              uma autorização, mas para mim não é uma dica legal."*

              Ele está certo nas duas metades. A ausência ALINHAVA MAL (a
              estrela do pedido caía onde ficava o checkbox do aviso, e as
              colunas das duas linhas não batiam na lista "Tudo"), e usar um
              buraco como sinal é o pior tipo de dica: quem não sabe da regra vê
              defeito, não informação. Desabilitado, ele segura a coluna e
              continua dizendo que ali não há o que marcar. */}
          {/* EXPERIÊNCIA (30/08): a bolinha do pedido saiu de cima da foto e
              ocupou a coluna do checkbox — que aqui estava desabilitada, ou
              seja, gastando espaço para não fazer nada. *"Desloca essa bolinha
              que a gente fez, tira de cima da foto e coloca no lugar do
              checkbox, só para a gente ver como fica."*

              O que se ganha, se ficar bom: a coluna 1 passa a dizer, na mesma
              posição em todas as linhas, **o que é aquela linha** — checkbox
              quando dá para agir em lote, selo quando é pedido. E a foto fica
              limpa.
              O que se perde: o selo deixa de estar colado à pessoa, então
              "quem" e "o quê" voltam a ser duas leituras.
              Para desfazer: este bloco volta a ser o `<input disabled>` e o
              `casaa-inbox-selo` volta para dentro do `casaa-inbox-avatar`. */}
          <div className="col-auto casaa-inbox-controle">
            <span className="casaa-inbox-selo casaa-inbox-selo-solto" title="Pedido de autorização">
              <i className={`bi ${ICONE_AUTORIZACAO}`}></i>
            </span>
          </div>
          <div className="col-auto casaa-inbox-controle">
            <button
              type="button"
              className="btn btn-link border-0"
              title={a.favorito ? 'Tirar dos favoritos' : 'Marcar como favorito'}
              aria-pressed={a.favorito}
              onClick={() => favoritarPedido(a)}
            >
              <i className={`bi ${a.favorito ? 'bi-star-fill text-warning' : 'bi-star text-secondary'}`}></i>
            </button>
          </div>
          {/* O ROSTO DE QUEM PEDE — ideia dele: numa fila de decisão, "quem
              pediu" pesa na leitura tanto quanto o valor. */}
          <div className="col-auto">
            <CustomAvatarSelo foto={a.solicitante_foto} nome={quemPede} icone={ICONE_AUTORIZACAO} titulo="Pedido de autorização" />
          </div>
          <div
            className="col-12 col-md"
            role="button"
            onClick={() => navigate(`/autorizacoes/${a.id}`)}
          >
            <p className="mb-0">
              {a.titulo}
              {quanto !== '—' && <strong className="text-body"> · {quanto}</strong>}
            </p>
            {/* OS MESMOS CAMPOS DA TABELA DE AUTORIZAÇÕES, NA MESMA ORDEM:
                título, valor, tipo, quem pede, vínculo. *"Esse não é o mesmo
                modelo que você está usando dentro da seção de autorizações;
                isso tem que ser definido, tem que ser padronizado"* (30/08).

                O FORMATO continua diferente — lá é tabela de gestão (comparável
                coluna a coluna), aqui é lista de caixa de entrada (rosto, uma
                linha por coisa). Isso é a tela, não o dado. O que não podia
                diferir era a INFORMAÇÃO e a ordem dela, e agora não difere: as
                duas leem os mesmos campos, pela mesma função. */}
            <p className="text-secondary text-truncated mb-0">
              {a.tipo_nome ? `${a.tipo_nome} · ` : ''}
              {quemPede || '—'}
              {a.entidade_label ? <> · <strong className="text-body">{a.entidade_label}</strong></> : null}
            </p>
          </div>
          <div className="col-auto">
            {/* O selo vem de `shared.ts`. Ver o cabecalho do arquivo. */}
            <span className={`badge ${badge(varianteStatus(st))}`}>{rotuloStatus(st)}</span>
          </div>
          <div className="col-auto text-end ms-auto">
            <p className="mb-0 small">
              {t.dia} <span className="text-secondary">{t.hora}</span>
            </p>
            {parado && st === 'pendente' && (
              <p className={`mb-0 small ${parado.tenso ? 'text-danger' : 'text-secondary'}`}>
                {parado.texto}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  function vazio(texto: string, icone = 'bi-check2-circle') {
    return (
      <div className="text-center py-5">
        <span className="avatar avatar-60 rounded-circle bg-theme-1-subtle text-theme-1 d-inline-flex align-items-center justify-content-center mb-3">
          <i className={`bi ${icone} h4 mb-0`}></i>
        </span>
        <p className="text-secondary mb-0">{texto}</p>
      </div>
    );
  }

  /** Uma entrada da coluna — a mesma marcação do molde (`avatar-28` + badge). */
  function entrada({ id, rotulo, icone }: { id: Filtro; rotulo: string; icone: string }) {
    const n = contador(id);
    return (
      <li className="nav-item" key={id}>
        <a
          className={`nav-link ${filtro === id ? 'active' : ''}`}
          role="button"
          onClick={() => trocarFiltro(id)}
        >
          <div className="avatar avatar-28 icon rounded-circle"><i className={`bi ${icone}`}></i></div>
          <div className="col">{rotulo}</div>
          {n > 0 && (
            <div className="col-auto align-self-center">
              <span className="badge text-bg-warning">{n > 99 ? '99+' : n}</span>
            </div>
          )}
        </a>
      </li>
    );
  }

  const inicio = total === 0 ? 0 : paginaAtual * porPagina + 1;
  const fim = Math.min((paginaAtual + 1) * porPagina, total);

  return (
    /* A BUSCA DESTA TELA VIRA A `busca` DA MOLDURA. Era um `input-group` próprio,
       com a lupa à DIREITA — uma das três buscas diferentes que o app tinha. O
       desenho agora é um só, e o motivo de a busca ficar aqui em cima continua
       valendo: o molde tem duas buscas possíveis, e duas na mesma tela é uma
       pergunta a mais ("esta procura só na pasta ou em tudo?"). */
    <CustomMolduraPagina
      titulo="Inbox"
      descricao="O que chegou para você, e o que está esperando."
      busca={{
        valor: busca,
        aoMudar: setBusca,
        placeholder: 'Buscar por assunto, pessoa ou OS…',
      }}
    >

      {/* A BARRA DE AÇÕES.
          ⚠️ AS AÇÕES FICAM SEMPRE À VISTA, DESABILITADAS SEM SELEÇÃO — pedido
          dele em 30/08. Botão que NASCE ao selecionar ensina duas vezes:
          primeiro a pessoa não sabe que a seleção serve para alguma coisa;
          depois a barra pula de tamanho no clique e o alvo se desloca.
          ⚠️ E TODO ÍCONE TEM `title` — *"os ícones acima do painelzinho têm que
          ter um tooltip indicando o que é"*. Ícone mudo é adivinhação. */}
      <ul className="nav rounded p-1 mb-3 align-items-center casaa-inbox-barra">
        <li className="nav-item d-none d-lg-block">
          <button
            type="button"
            className="btn btn-link btn-square innersidebar-btn"
            onClick={() => setLateralFechada((v) => !v)}
            title={lateralFechada ? 'Mostrar o painel' : 'Recolher o painel'}
          >
            <i className="bi bi-layout-sidebar h5 align-middle"></i>
          </button>
        </li>
        {/* SELECIONAR TODOS — um botão só, e o `✓✓` sozinho.
            *"Não precisa do 'Todos' se já tem aquele símbolo dos dois Vs. Bota
            aquele símbolo como um botãozinho para ele não bagunçar, tira o
            check e tira o 'Todos'."* (30/08) — e ele tem razão: eram três
            coisas dizendo a mesma, num canto onde só cabe uma.

            ⚠️ O QUE O CHECKBOX FAZIA E UM BOTÃO NÃO FAZ SOZINHO: mostrar
            ESTADO. Marcado, indeterminado, vazio — o checkbox diz os três de
            graça. Por isso este botão **alterna** e fica **aceso** (`active`)
            quando tudo está marcado: sem esse retorno, o segundo clique é
            loteria, e a pessoa marca tudo achando que desmarcou. O contador à
            direita ("N selecionados") é o segundo sinal, para quem não confia
            no primeiro.

            ⚠️ E o `✓✓` só é legível AQUI porque não há outro na barra: ele foi
            "marcar como lido" por dois dias, e ele o leu como selecionar-tudo
            as duas vezes. O símbolo não era ruim; estava no lugar errado. */}
        {/* O "selecionar todos" aparece sempre que houver algo selecionável.
            Antes ele sumia nas pastas de autorização, que não existem mais —
            agora qualquer pasta pode ter pedido dentro, e quem separa o que se
            pode marcar é `avisosDaPagina`, não o nome da pasta. */}
        {avisosDaPagina.length > 0 && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn btn-link nav-link px-lg-3 ${todosMarcados ? 'casaa-inbox-barra-ligado' : ''}`}
              onClick={() => setMarcados(todosMarcados ? [] : avisosDaPagina.map((a) => a.id))}
              disabled={avisosDaPagina.length === 0}
              aria-pressed={todosMarcados}
              title={todosMarcados ? 'Desmarcar todos' : 'Selecionar todos'}
            >
              <i className="bi bi-check2-all h5 align-middle"></i>
            </button>
          </li>
        )}
        <li className="nav-item">
          <button type="button" className="btn btn-link nav-link px-lg-3" onClick={carregar} title="Recarregar">
            <i className="bi bi-arrow-clockwise h5 align-middle"></i>
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className="btn btn-link nav-link px-lg-3"
            onClick={() => setConfirmarLote(true)}
            disabled={!temSelecao}
            title={temSelecao ? 'Arquivar os selecionados' : 'Selecione para arquivar'}
          >
            <i className="bi bi-archive h5 align-middle"></i>
          </button>
        </li>
        <li className="nav-item dropdown">
          {/* O `⋮` do molde. "Marcar TUDO como lido" mora aqui, escrito por
              extenso, porque é ação de lista inteira: rara, sem volta, e que
              nenhum ícone consegue distinguir de "marcar os selecionados". */}
          <button
            type="button"
            className="btn btn-link nav-link px-lg-3"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="Mais ações"
          >
            <i className="bi bi-three-dots-vertical h5 align-middle"></i>
          </button>
          <ul className="dropdown-menu">
            <li>
              <button
                type="button"
                className="dropdown-item"
                onClick={marcarLidosMarcados}
                disabled={!temSelecao}
              >
                Marcar os selecionados como lidos
              </button>
            </li>
            <li>
              <button
                type="button"
                className="dropdown-item"
                onClick={marcarTudoLido}
                disabled={!temNaoLido}
              >
                Marcar tudo como lido
              </button>
            </li>
          </ul>
        </li>

        {/* À DIREITA: quantos, e a paginação do molde. */}
        <li className="nav-item ms-auto">
          <span className="nav-link text-secondary small">
            {temSelecao
              ? (marcados.length === 1 ? '1 selecionado' : `${marcados.length} selecionados`)
              : `${inicio}–${fim} de ${total}`}
          </span>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className="btn btn-link btn-square"
            onClick={() => irParaPagina(Math.max(0, pagina - 1))}
            disabled={paginaAtual === 0}
            title="Página anterior"
          >
            <i className="bi bi-chevron-left align-middle"></i>
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className="btn btn-link btn-square"
            onClick={() => irParaPagina(Math.min(paginas - 1, pagina + 1))}
            disabled={paginaAtual >= paginas - 1}
            title="Próxima página"
          >
            <i className="bi bi-chevron-right align-middle"></i>
          </button>
        </li>
      </ul>

      {/* O SEGUNDO EIXO — que TIPO de coisa.
          *"A gente tem os avisos e a gente tem as autorizações, e eu quero que
          isso continue separado… a autorização é muito importante e ela merece
          destaque."* (30/08)

          ⚠️ Faixa, e NÃO pasta nova. A coluna responde "o que se espera de
          você"; isto responde "que coisa é". Como são perguntas diferentes,
          elas se COMBINAM — "Para resolver" + "Autorizações" são os pedidos
          parados, sem precisar de uma pasta chamada `Autorizações › Pendentes`
          ao lado de outra dizendo quase o mesmo. Foi essa mistura de eixos num
          menu só que produziu as nove pastas.

          O contador é DENTRO DA PASTA ABERTA, nunca do total: uma aba que diz
          "3" e mostra 3 é a única que não confunde. */}
      <ul className="nav nav-pills gap-1 mb-3 casaa-inbox-tipos">
        {TIPOS.map(({ id, rotulo, icone }) => {
          const n = ix.contadorTipo(id);
          return (
            <li className="nav-item" key={id}>
              <button
                type="button"
                className={`nav-link d-flex align-items-center gap-2 ${ix.tipo === id ? 'active' : ''}`}
                onClick={() => ix.trocarTipo(id)}
              >
                <i className={`bi ${icone}`}></i>
                <span>{rotulo}</span>
                {n > 0 && <span className="badge text-bg-warning">{n > 99 ? '99+' : n}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      {recado && (
        <div className="alert alert-info alert-dismissible" role="status">
          {recado}
          <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setRecado('')}></button>
        </div>
      )}

      <div className="inner-sidebar-wrap">
        <div
          className="inner-sidebar"
          style={{ '--h-dynamic': 'auto' } as React.CSSProperties}
        >
          <div className="card adminuiux-card h-100">
            <div className="card-body">
              {GRUPOS.map((g) => (
                <div key={g.titulo || 'topo'}>
                  {g.titulo && (
                    <p className="small text-secondary mt-3 mb-2">{g.titulo}</p>
                  )}
                  <ul className="nav flex-column">{g.entradas.map(entrada)}</ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="inner-sidebar-content">
          {/* No CELULAR a coluna sai da frente (é o próprio tema que a esconde
              abaixo de 992px) e o filtro vira pills que ROLAM — `overflow-x-auto`
              e `flex-nowrap` JUNTOS. `flex-nowrap` sozinho espreme. */}
          <ul className="nav nav-pills overflow-x-auto w-100 text-nowrap flex-nowrap pb-2 mb-2 d-lg-none">
            {TODAS_ENTRADAS.map(({ id, rotulo }) => (
              <li className="nav-item flex-shrink-0" key={id}>
                <button
                  type="button"
                  className={`nav-link text-nowrap ${filtro === id ? 'active' : ''}`}
                  onClick={() => trocarFiltro(id)}
                >
                  {rotulo}
                </button>
              </li>
            ))}
          </ul>

          <div className="list-group adminuiux-list-group list-group-flush bg-none casaa-inbox-lista">
            {carregando ? (
              <div className="placeholder-glow p-3">
                <span className="placeholder col-7 mb-2"></span>
                <span className="placeholder col-4"></span>
              </div>
            ) : naTela.length > 0 ? (
              naTela.map((it) => (it.aviso ? linhaAviso(it.aviso) : linhaAutorizacao(it.pedido!)))
            ) : filtro === 'favoritos' ? (
              vazio('Você ainda não marcou nenhum favorito. A estrela de cada linha marca.', 'bi-star')
            ) : filtro === 'arquivados' ? (
              vazio('Nada arquivado ainda. O que você arquivar fica aqui — nada é apagado.', 'bi-archive')
            ) : (
              vazio(busca ? 'Nada encontrado.' : 'Nada por aqui.', 'bi-inbox')
            )}
          </div>
        </div>
      </div>

      {/* CONFIRMAR O LOTE -- "se voce vai arquivar, ainda mais em massa, tem
          que ter um botao de confirmacao, um modal padronizado do tema"
          (30/08). `casaa-modal` e a classe da casa: sem ela o modal sai do
          react-bootstrap com as linhas divisorias que nenhuma tela do AdminUX
          tem (ver `estilos/_modal.scss`). */}
      <Modal
        className="casaa-modal"
        show={confirmarLote}
        onHide={() => setConfirmarLote(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title as="h6" className="mb-0">Arquivar o que esta selecionado?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            {marcados.length === 1
              ? 'Um aviso sai da lista.'
              : `${marcados.length} avisos saem da lista.`}
          </p>
          <p className="text-secondary small mb-0">
            Nada e apagado: o que for arquivado continua na pasta{' '}
            <strong>Arquivados</strong>. Decisao em aberto e favorito nao vao
            junto.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setConfirmarLote(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-theme-1" onClick={confirmarEArquivar}>
            Arquivar
          </button>
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
